import {
  SETTING_VALUE_SCHEMAS,
  normalizeLeadDays,
  sameJsonValue,
  SettingKey,
  settingId,
  type SettingData,
  type SettingRecord,
  type SettingValue,
  RecordType,
} from '@wiztime/core';
import { ValidationError } from '../errors';
import type { WriteSession } from './session';

/**
 * Change a synced setting (deterministic id per key). The personal-day start is refused here
 * because changing it re-homes blocks: use `changeDayStart`.
 */
export async function setSetting<K extends SettingKey>(
  session: WriteSession,
  key: K,
  value: SettingValue<K>,
): Promise<SettingRecord> {
  return writeSetting(session, key, value);
}

/**
 * Change several settings together, as one transaction and one undo step. Every value is checked
 * and written in the order given; the first invalid one fails the whole command and nothing is
 * stored. Entries that already hold their value write nothing.
 */
export async function setSettings(
  session: WriteSession,
  entries: readonly SettingData[],
): Promise<SettingRecord[]> {
  const records: SettingRecord[] = [];
  for (const entry of entries) records.push(await writeSetting(session, entry.key, entry.value));
  return records;
}

async function writeSetting<K extends SettingKey>(
  session: WriteSession,
  key: K,
  value: SettingValue<K>,
): Promise<SettingRecord> {
  if (key === SettingKey.DayStartMin) {
    throw new ValidationError('Use changeDayStart to change the personal-day start');
  }
  const parsed = SETTING_VALUE_SCHEMAS[key].safeParse(value);
  if (!parsed.success) {
    throw new ValidationError(
      `Invalid value for setting ${key}`,
      parsed.error.issues.map((issue) => issue.message),
    );
  }
  const stored = canonical(key, parsed.data);
  const id = settingId(key);
  const existing = await session.find(id);
  if (
    existing !== null &&
    existing.type === RecordType.Setting &&
    existing.data?.key === key &&
    sameJsonValue(existing.data.value, stored)
  ) {
    return existing;
  }
  return session.write(RecordType.Setting, id, { key, value: stored } as SettingData);
}

/** The form in which a value is stored: list settings are written normalised (distinct; lead days also ascending). */
function canonical(key: SettingKey, value: unknown): unknown {
  if (key === SettingKey.DefaultLeadDays) return normalizeLeadDays(value as number[]);
  if (key === SettingKey.HolidayPacks) return [...new Set(value as string[])];
  return value;
}
